import { useStore } from 'zustand'
import { engine } from '@/runtime'
import { useUi } from '@/state/ui'
import type { SyncStatus } from '@/db/sync'

const LABEL = {
  synced: 'Synced',
  pending: 'Changes waiting to sync',
  syncing: 'Syncing…',
  offline: 'Offline — changes are saved here',
  paused: 'Sync paused — check the token in Settings',
  error: 'Sync error',
} as const

/** What a tap on the dot says, when there is something to say. */
const EXPLAIN: Partial<Record<SyncStatus, string>> = {
  offline: "Offline. What you change is kept on this device and sent when you're connected.",
  paused: 'Sync is paused: the server refused the token. Check it in Settings.',
  error: 'Sync hit an error. Your changes are kept here; it tries again by itself.',
  pending: 'Changes are on their way to the server.',
}

/** States in which a change made here may sit unsent for a while (not the moment before a push). */
const HELD: SyncStatus[] = ['offline', 'paused', 'error']

/**
 * Small dot: synced / pending / offline. Never modal, never blocking. `quiet`
 * hides it while everything is synced (the phone, where it sits in the tab
 * bar's edge). A tap syncs, and says in words what a grey or red dot means.
 */
export const SyncDot = ({ quiet = false }: { quiet?: boolean }) => {
  const status = useStore(engine.store, (s) => s.status)
  const live = useStore(engine.store, (s) => s.live)
  const flash = useUi((s) => s.showFlash)
  if (quiet && status === 'synced') return null
  const color =
    status === 'synced'
      ? 'bg-emerald-400'
      : status === 'syncing' || status === 'pending'
        ? 'bg-amber-300'
        : status === 'offline'
          ? 'bg-faint'
          : 'bg-rose-400'
  return (
    <button
      type="button"
      onClick={() => {
        const say = EXPLAIN[status]
        if (say) flash(say, 5000)
        void engine.sync()
      }}
      className="flex items-center gap-2 text-xs text-faint min-h-8 px-2 rounded hover:text-fg-2"
      title={live && status === 'synced' ? 'Synced · live' : LABEL[status]}
      aria-label={live && status === 'synced' ? 'Synced, live' : LABEL[status]}
      data-sync-status={status}
      data-live={live ? 'yes' : 'no'}
    >
      <span
        className={`inline-block h-2 w-2 rounded-full ${color} ${status === 'syncing' ? 'animate-pulse' : ''}`}
      />
    </button>
  )
}

/**
 * A quiet mark on a row whose change has not reached the server yet: shown
 * while sync is held up (offline, paused, an error), never in the half second
 * before an ordinary push, so a row does not flicker on every edit.
 */
export const UnsentMark = ({ id, className }: { id: string; className?: string }) => {
  const held = useStore(engine.store, (s) => HELD.includes(s.status) && s.unsent.includes(id))
  if (!held) return null
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] text-faint ${className ?? ''}`}
      title="Kept on this device; sent when you're connected"
      data-testid="unsent-mark"
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full border border-current" aria-hidden />
      Not sent yet
    </span>
  )
}
